import React, { useEffect, useRef, memo, useCallback } from 'react';
import { Code, Copy, Save } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface CertificateCodeEditorProps {
  htmlContent: string;
  onChange: (value: string) => void;
  onSave: () => void;
  isSaving?: boolean;
}

export const CertificateCodeEditor = memo(function CertificateCodeEditor({ 
  htmlContent, 
  onChange,
  onSave,
  isSaving = false
}: CertificateCodeEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMountedRef = useRef<boolean>(true);
  
  useEffect(() => {
    isMountedRef.current = true;
    
    const timer = setTimeout(() => {
      if (textareaRef.current && isMountedRef.current) {
        textareaRef.current.focus();
      }
    }, 100);
    
    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
    };
  }, []);
  
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle Ctrl/Cmd + S for save
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      onSave();
      return;
    }
    
    // Handle tab key
    if (e.key === 'Tab') {
      e.preventDefault();
      
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      const newValue = textarea.value.substring(0, start) + '  ' + textarea.value.substring(end);
      
      textarea.value = newValue;
      textarea.selectionStart = textarea.selectionEnd = start + 2;
      
      onChange(newValue);
    }
  }, [onChange, onSave]);
  
  const copyToClipboard = useCallback(() => {
    if (navigator.clipboard && htmlContent) {
      navigator.clipboard.writeText(htmlContent)
        .then(() => toast.success("HTML copied to clipboard"))
        .catch(() => toast.error("Failed to copy HTML"));
    }
  }, [htmlContent]);
  
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  }, [onChange]);
  
  return (
    <div className="h-full flex flex-col bg-background">
      <div className="h-[50px] flex-none p-3 bg-muted/50 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Code className="h-4 w-4" />
          <h3 className="text-sm font-medium">HTML Editor</h3>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onSave}
            disabled={isSaving}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={copyToClipboard} 
            title="Copy HTML"
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <div className="flex-1 relative overflow-auto">
        <Textarea 
          ref={textareaRef}
          value={htmlContent}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter your certificate HTML here..."
          className="h-full w-full resize-none border-0 focus-visible:ring-0 font-mono text-sm p-4"
          style={{
            minHeight: '100%',
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace'
          }}
        />
      </div>
      
      <div className="h-[30px] flex-none px-3 py-1 bg-muted/30 border-t flex items-center justify-between text-xs text-muted-foreground">
        <span>Use <code>{'{{student_name}}'}</code>, <code>{'{{course_name}}'}</code>, <code>{'{{completion_date}}'}</code>, <code>{'{{certificate_number}}'}</code></span>
        <span>Press Ctrl+S (Cmd+S) to save</span>
      </div>
    </div>
  );
});
